import React from 'react'
import { Sidebar, Input } from "@iq/models"

import { LuSave, LuZap } from "react-icons/lu"
import { createProduct } from "../../services/productService"
import "./createProductModalStyles.css"
const noop = () => {};

/**
 * Edit callbacks receive { name, description, attributes: string[] } and may be async.
 * Reject to keep the sidebar open with its draft and display the error.
 * Mount a new edit session with initialProduct; existing attribute names are read-only.
 */
export default function CreateProductModal({
    isOpen,
    onClose,
    onCreated,
    onNext,
    isEditing = false,
    initialProduct,
    onSaveChanges,
    onContinueEditing,
    attributes = [],
    onAttributesChange = noop,
    onProductNameChange = noop,
    onProductDescriptionChange = noop,
    resetToken = 0,
}) {
  const [productName, setProductName] = React.useState(isEditing ? initialProduct?.name || "" : "");
  const [productDescription, setProductDescription] = React.useState(isEditing ? initialProduct?.description || "" : "");
  const [saving, setSaving] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");

  React.useEffect(() => {
    if (isEditing) return;
    setProductName("");
    setProductDescription("");
    setErrorMessage("");
    onProductNameChange("");
    onProductDescriptionChange("");
    onAttributesChange([]);
  }, [isEditing, onAttributesChange, onProductDescriptionChange, onProductNameChange, resetToken]);

  const existingAttributes = isEditing ? initialProduct?.attributes || [] : [];

  const handleEditingAction = async (continueToVariants) => {
    if (saving) return;
    const name = productName.trim();
    if (!name) {
      setErrorMessage("Enter a product name.");
      return;
    }
    setSaving(true);
    setErrorMessage("");
    try {
      const handler = continueToVariants ? onContinueEditing : onSaveChanges;
      if (!handler) {
        throw new Error("Saving product changes is not connected yet.");
      }
      await handler({
        name,
        description: productDescription.trim(),
        attributes: Array.from(new Set([...existingAttributes, ...attributes].map((attribute) => attribute.trim()).filter(Boolean))),
      });
      onClose?.();
    } catch (error) {
      setErrorMessage(error?.message || "Failed to update product.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = React.useCallback(async () => {
    const name = productName.trim();
    const description = productDescription.trim();

    if (!name || !description) {
      setErrorMessage("Name and description are required.");
      return;
    }

    setSaving(true);
    setErrorMessage("");

    try {
      await createProduct({
        name,
        description,
      });

      onCreated?.();
      onClose?.();
    } catch (error) {
      setErrorMessage(error?.message || "Failed to create product.");
    } finally {
      setProductName("");
      setProductDescription("");
      onAttributesChange([]);
      setSaving(false);
    }
  }, [onAttributesChange, onClose, onCreated, productDescription, productName]);

  const handleNext = React.useCallback(() => {
    if (!productName.trim()){
      return;
    }
    onNext?.();
    onClose?.();
  }, [onClose, onNext, productName]);

  const isSaveDisabled = saving || !productName.trim();

  const actionButtons = [
    {
      key: isEditing ? "save-changes" : "save-draft",
      text: saving ? "Saving..." : isEditing ? "Save Changes" : "Save Draft",
      icon: <LuSave />,
      onClick: isEditing ? () => handleEditingAction(false) : handleSaveDraft,
      disabled: isSaveDisabled,
      style: { 
        backgroundColor: "var(--surface-2)",
        color: "var(--fg-default)",
        border: "1px solid var(--border-default)"
      }
    },
    {
      key: "next",
      text: "Continue to Variants",
      icon: <LuZap />,
      onClick: isEditing ? () => handleEditingAction(true) : handleNext,
      closeOnClick: !isEditing,
      disabled: saving || !productName.trim(),
    },
  ];

  const handleProductNameChange = React.useCallback((e) => {
    const value = e.target.value;
    setProductName(value);
    onProductNameChange(value);
  }, [onProductNameChange]);

  const handleProductDescriptionChange = React.useCallback((e) => {
    const value = e.target.value;
    setProductDescription(value);
    onProductDescriptionChange(value);
  }, [onProductDescriptionChange]);

  return (
    <Sidebar
      isOpen={isOpen}
      onClose={() => { if (!saving) onClose?.(); }}
      title={isEditing ? "Edit Product" : "Create Product"}
      description="Add details and attribute names. Assign values when creating variants."
      actionButtons={actionButtons}
    >
      <div className="createProductModalContainer">
          <Input 
            label="Product Name"
            type="text"
            placeholder="Ex: 'Oversized Hoodie'"
            value={productName}
            onChange={handleProductNameChange}
            disabled={saving}
          />
          <Input
            label="Product Description"
            type="textarea"
            placeholder="Ex: 'A cozy oversized hoodie for all seasons.'"
            value={productDescription}
            onChange={handleProductDescriptionChange}
            disabled={saving}
          />
          <Input 
            label="Attributes"
            type="addItem"
            placeholder="Ex: 'Color'"
            addItem={{
              items: attributes,
              lockedItems: existingAttributes,
              onItemsChange: (names) => onAttributesChange(isEditing ? Array.from(new Set([...existingAttributes, ...names])) : names),
            }}
            disabled={saving}
          />
          {errorMessage && <p role="alert">{errorMessage}</p>}
      </div>
  </Sidebar>
  )
}
